/**
 * ChannelsPage — управление каналами (Email, Telegram, Widget).
 *
 * Phase 6 Étap 2: Minimum Viable Onboarding
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Spinner, Card, Badge, OnboardingHint } from '../components/ui';
import { useChannelsStatus, useWidgetConfig, useCreateWidgetConfig } from '../api/channels';
import { WidgetSettings } from '../components/channels/WidgetSettings';
import { MailboxCard, AddMailboxForm } from '../components/mailboxes';
import { useMailboxes } from '../api/mailboxes';

// ============================================================================
// Types
// ============================================================================

type ChannelTab = 'email' | 'telegram' | 'widget';

// ============================================================================
// ChannelsPage
// ============================================================================

export function ChannelsPage(): JSX.Element {
  const [activeTab, setActiveTab] = useState<ChannelTab>('email');
  const { data: status, isLoading } = useChannelsStatus();

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-base">
        <Spinner size="lg" className="text-accent-brand" />
      </div>
    );
  }

  // Подсчёт подключённых каналов для badges
  const emailCount = status?.email.length ?? 0;
  const tgConnected = status?.telegram.configured ?? false;
  const widgetConnected = status?.widget.configured ?? false;

  return (
    <div className="h-full bg-surface-base overflow-auto">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Заголовок */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-semibold text-text-primary">
            Каналы
          </h1>
          <p className="text-text-secondary text-sm mt-1">
            Подключённые каналы связи с клиентами
          </p>
        </div>

        {/* Onboarding hint — когда есть хотя бы один канал */}
        {(emailCount > 0 || tgConnected || widgetConnected) && (
          <OnboardingHint hintId="channels-connected" icon="🎉" className="mb-6">
            Канал подключён! Перейдите во{' '}
            <Link to="/inbox" className="font-medium text-accent-brand underline underline-offset-2">
              Входящие
            </Link>
            , чтобы увидеть диалоги и черновики Anima.
            Anima учится на каждом вашем решении — принять, отредактировать или отклонить.
          </OnboardingHint>
        )}

        {/* Табы */}
        <div className="flex gap-1 mb-6 bg-surface-subtle rounded-comp p-1">
          <TabButton
            active={activeTab === 'email'}
            onClick={() => setActiveTab('email')}
            label="Email"
            count={emailCount}
            connected={emailCount > 0}
          />
          <TabButton
            active={activeTab === 'telegram'}
            onClick={() => setActiveTab('telegram')}
            label="Telegram"
            connected={tgConnected}
          />
          <TabButton
            active={activeTab === 'widget'}
            onClick={() => setActiveTab('widget')}
            label="Widget"
            connected={widgetConnected}
          />
        </div>

        {/* Контент таба */}
        {activeTab === 'email' && <EmailTab />}
        {activeTab === 'telegram' && <TelegramTab status={status?.telegram} />}
        {activeTab === 'widget' && <WidgetTab />}
      </div>
    </div>
  );
}

// ============================================================================
// TabButton
// ============================================================================

function TabButton({
  active,
  onClick,
  label,
  count,
  connected,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
  connected: boolean;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-comp text-sm font-medium transition-all ${
        active
          ? 'bg-white text-text-primary shadow-sm'
          : 'text-text-secondary hover:text-text-primary'
      }`}
    >
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
        connected ? 'bg-accent-success' : 'bg-text-muted'
      }`} />
      {label}
      {count !== undefined && count > 0 && (
        <span className="text-xs bg-surface-subtle px-1.5 py-0.5 rounded-full text-text-secondary">
          {count}
        </span>
      )}
    </button>
  );
}

// ============================================================================
// EmailTab
// ============================================================================

function EmailTab(): JSX.Element {
  const [showAddForm, setShowAddForm] = useState(false);
  const { data: mailboxes, isLoading, isError, refetch } = useMailboxes();
  const navigate = useNavigate();

  const handleAddSuccess = () => {
    setShowAddForm(false);
  };

  return (
    <div>
      {/* Кнопка добавления */}
      {!showAddForm && (
        <div className="flex justify-end mb-4">
          <Button variant="primary" size="md" onClick={() => setShowAddForm(true)}>
            <span className="flex items-center gap-2">
              <PlusIcon />
              Добавить ящик
            </span>
          </Button>
        </div>
      )}

      {/* Форма добавления */}
      {showAddForm && (
        <div className="mb-6 p-6 bg-surface-elevated rounded-panel shadow-subtle border border-border-subtle">
          <h2 className="font-heading text-xl font-semibold text-text-primary mb-6">
            Новый почтовый ящик
          </h2>
          <AddMailboxForm
            onSuccess={handleAddSuccess}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}

      {/* Список */}
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState onRetry={() => refetch()} message="Не удалось загрузить почтовые ящики" />
      ) : mailboxes && mailboxes.length > 0 ? (
        <div className="space-y-4">
          {/* Тот же расчёт максимума стоял и здесь — знание жило в двух
              страницах и в обеих прятало сорвавшиеся папки. Сводку собирает
              сам компонент */}
          {mailboxes.map((mailbox) => (
            <MailboxCard
              key={mailbox.id}
              mailbox={mailbox}
              onOpen={() => navigate('/inbox?channel=email')}
            />
          ))}
        </div>
      ) : (
        <EmptyChannelState
          icon={<EmailIcon />}
          title="Нет подключённых ящиков"
          description="Подключите первый почтовый ящик, чтобы начать получать и обрабатывать письма"
          action={!showAddForm ? (
            <Button variant="primary" size="md" onClick={() => setShowAddForm(true)}>
              <span className="flex items-center gap-2">
                <PlusIcon />
                Добавить первый ящик
              </span>
            </Button>
          ) : undefined}
        />
      )}
    </div>
  );
}

// ============================================================================
// TelegramTab
// ============================================================================

function TelegramTab({ status }: { status: { configured: boolean; botUsername: string | null; activeChatCount: number } | undefined }): JSX.Element {
  const navigate = useNavigate();
  if (!status?.configured) {
    return (
      <EmptyChannelState
        icon={<TelegramIcon />}
        title="Telegram не настроен"
        description="Для подключения Telegram нужно создать бота через @BotFather и указать токен в настройках сервера"
        action={
          <div className="text-sm text-text-secondary bg-surface-subtle rounded-comp p-4 text-left max-w-md">
            <p className="font-medium text-text-primary mb-2">Как подключить:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Откройте @BotFather в Telegram</li>
              <li>Создайте бота: <code className="text-xs bg-surface-base px-1 py-0.5 rounded">/newbot</code></li>
              <li>Скопируйте токен бота</li>
              <li>Укажите токен в <code className="text-xs bg-surface-base px-1 py-0.5 rounded">TELEGRAM_BOT_TOKEN</code></li>
            </ol>
          </div>
        }
      />
    );
  }

  return (
    <Card className="border border-border-subtle">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-comp bg-[#229ED9]/10 flex items-center justify-center">
          <TelegramIcon />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-text-primary">Telegram Bot</h3>
            <Badge variant="active">Подключён</Badge>
          </div>
          {status.botUsername && (
            <p className="text-sm text-text-secondary mt-0.5">@{status.botUsername}</p>
          )}
          <p className="text-sm text-text-secondary mt-2">
            Активных чатов: <span className="font-medium text-text-primary">{status.activeChatCount}</span>
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => navigate('/inbox?channel=telegram')}>
          Входящие
        </Button>
      </div>
    </Card>
  );
}

// ============================================================================
// WidgetTab
// ============================================================================

function WidgetTab(): JSX.Element {
  const { data: config, isLoading } = useWidgetConfig();
  const createConfig = useCreateWidgetConfig();
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  if (isLoading) {
    return <LoadingState />;
  }

  if (!config) {
    return (
      <EmptyChannelState
        icon={<WidgetIcon />}
        title="Виджет не настроен"
        description="Создайте виджет для вашего сайта — клиенты смогут писать прямо со страницы"
        action={
          <Button
            variant="primary"
            size="md"
            onClick={() => createConfig.mutate({})}
            disabled={createConfig.isPending}
          >
            {createConfig.isPending ? 'Создание...' : 'Создать виджет'}
          </Button>
        }
      />
    );
  }

  const scriptSnippet = `<script src="${window.location.origin}/api/widget/anima-widget.js"
  data-widget-key="${config.widgetKey}"
  data-api-base="${window.location.origin}">
</script>`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(scriptSnippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API недоступен (HTTP, iframe)
    }
  };

  return (
    <div className="space-y-4">
      <Card className="border border-border-subtle">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-comp bg-accent-brand/10 flex items-center justify-center">
            <WidgetIcon />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-text-primary">{config.name}</h3>
              <Badge variant={config.enabled ? 'active' : 'waiting'}>
                {config.enabled ? 'Активен' : 'Выключен'}
              </Badge>
            </div>
            <p className="text-sm text-text-secondary mt-0.5 font-mono truncate">
              {config.widgetKey}
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => navigate('/inbox?channel=widget')}>
            Входящие
          </Button>
        </div>
      </Card>

      {/* Snippet для вставки */}
      <Card className="border border-border-subtle">
        <h3 className="font-medium text-text-primary mb-3">Код для вставки на сайт</h3>
        <div className="relative">
          <pre className="bg-surface-subtle rounded-comp p-4 text-xs text-text-secondary overflow-x-auto font-mono">
            {scriptSnippet}
          </pre>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCopy}
            className="absolute top-2 right-2"
          >
            {copied ? 'Скопировано!' : 'Копировать'}
          </Button>
        </div>
      </Card>

      {/* Настройки кастомизации */}
      <WidgetSettings config={config} />
    </div>
  );
}

// ============================================================================
// Shared components
// ============================================================================

function EmptyChannelState({
  icon,
  title,
  description,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="w-20 h-20 rounded-full bg-surface-subtle border border-border-subtle flex items-center justify-center mb-6">
        {icon}
      </div>
      <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">{title}</h3>
      <p className="text-text-secondary text-center max-w-sm mb-6">{description}</p>
      {action}
    </div>
  );
}

function LoadingState(): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <Spinner size="lg" className="text-accent-brand" />
      <p className="text-text-secondary mt-4">Загрузка...</p>
    </div>
  );
}

function ErrorState({ onRetry, message }: { onRetry: () => void; message: string }): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <p className="text-text-secondary mb-4">{message}</p>
      <Button variant="secondary" size="sm" onClick={onRetry}>Повторить</Button>
    </div>
  );
}

// ============================================================================
// Icons
// ============================================================================

function PlusIcon(): JSX.Element {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

function EmailIcon(): JSX.Element {
  return (
    <svg className="w-10 h-10 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
  );
}

function TelegramIcon(): JSX.Element {
  return (
    <svg className="w-6 h-6 text-[#229ED9]" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

function WidgetIcon(): JSX.Element {
  return (
    <svg className="w-10 h-10 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
    </svg>
  );
}
